import { useEffect, useState } from 'react';
import {
  Table,
  Card,
  Space,
  Select,
  Switch,
  Tag,
  Button,
  message,
  Typography,
} from 'antd';
import {
  ReloadOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { reportAPI, screenAPI, playlistAPI } from '../services/api';
import { themeColors } from '../config/theme';

const { Title } = Typography;

export default function Reports() {
  const [data, setData] = useState([]);
  const [screens, setScreens] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [screenId, setScreenId] = useState(null);
  const [playlistId, setPlaylistId] = useState(null);
  const [expiringSoon, setExpiringSoon] = useState(false);

  useEffect(() => {
    fetchScreens();
    fetchPlaylists();
  }, []);

  useEffect(() => {
    fetchReport();
  }, [screenId, playlistId, expiringSoon]);

  const fetchScreens = async () => {
    try {
      const response = await screenAPI.list({ limit: 100 });
      setScreens(response.data.data.data);
    } catch (error) {
      console.error('Failed to load screens');
    }
  };

  const fetchPlaylists = async () => {
    try {
      const response = await playlistAPI.list({ limit: 100 });
      setPlaylists(response.data.data.data);
    } catch (error) {
      console.error('Failed to load playlists');
    }
  };

  const fetchReport = async () => {
    try {
      setLoading(true);
      const params = {};
      if (screenId) params.screenId = screenId;
      if (playlistId) params.playlistId = playlistId;
      if (expiringSoon) params.expiringSoon = true;

      const response = await reportAPI.videoSchedule(params);
      setData(response.data.data);
    } catch (error) {
      message.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const formatTimeSlots = (timeSlots) => {
    if (typeof timeSlots === 'string') {
      try {
        timeSlots = JSON.parse(timeSlots);
      } catch {
        timeSlots = null;
      }
    }
    if (!Array.isArray(timeSlots) || timeSlots.length === 0) return 'All day';
    return timeSlots.map((slot) => `${slot.start}-${slot.end}`).join(', ');
  };

  const buildExportRows = () =>
    data.map((row) => ({
      'Video Title': row.video_title,
      Playlist: row.playlist_name,
      Screen: `${row.screen_name} (${row.screen_code})`,
      'Start Date': dayjs(row.start_datetime).format('YYYY-MM-DD HH:mm'),
      'End Date': dayjs(row.end_datetime).format('YYYY-MM-DD HH:mm'),
      'Time Slots': formatTimeSlots(row.time_slots),
      'Days Left': row.days_until_expiry,
      Status: row.schedule_status,
    }));

  const handleExportExcel = () => {
    const rows = buildExportRows();
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Video Report');
    XLSX.writeFile(workbook, `video-schedule-report-${dayjs().format('YYYY-MM-DD')}.xlsx`);
  };

  const handleExportPDF = () => {
    const rows = buildExportRows();
    const doc = new jsPDF({ orientation: 'landscape' });

    doc.setFontSize(14);
    doc.text('Video Schedule Report', 14, 15);

    autoTable(doc, {
      startY: 22,
      head: [Object.keys(rows[0] || {})],
      body: rows.map((row) => Object.values(row)),
      styles: { fontSize: 8 },
      headStyles: { fillColor: [249, 115, 22] },
    });

    doc.save(`video-schedule-report-${dayjs().format('YYYY-MM-DD')}.pdf`);
  };

  const columns = [
    {
      title: 'Video Title',
      dataIndex: 'video_title',
      key: 'video_title',
    },
    {
      title: 'Playlist',
      dataIndex: 'playlist_name',
      key: 'playlist_name',
    },
    {
      title: 'Screen',
      key: 'screen',
      render: (_, record) => `${record.screen_name} (${record.screen_code})`,
    },
    {
      title: 'Start Date',
      dataIndex: 'start_datetime',
      key: 'start_datetime',
      render: (value) => dayjs(value).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: 'End Date',
      dataIndex: 'end_datetime',
      key: 'end_datetime',
      render: (value) => dayjs(value).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: 'Time Slots',
      dataIndex: 'time_slots',
      key: 'time_slots',
      render: (value) => formatTimeSlots(value),
    },
    {
      title: 'Days Left',
      dataIndex: 'days_until_expiry',
      key: 'days_until_expiry',
      sorter: (a, b) => a.days_until_expiry - b.days_until_expiry,
      render: (value) => {
        if (value <= 3) {
          return (
            <Tag icon={<WarningOutlined />} color="error">
              {value} day{value === 1 ? '' : 's'}
            </Tag>
          );
        }
        return <Tag color="default">{value} days</Tag>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'schedule_status',
      key: 'schedule_status',
      render: (value) => (
        <Tag color={value === 'active' ? 'success' : 'processing'}>{value}</Tag>
      ),
    },
  ];

  return (
    <div>
      <Title level={3} style={{ color: themeColors.text }}>
        Video Schedule Report
      </Title>

      <Card style={{ marginBottom: 16 }}>
        <Space wrap size="middle">
          <Select
            placeholder="Filter by Screen"
            allowClear
            style={{ width: 220 }}
            value={screenId}
            onChange={setScreenId}
            options={screens.map((s) => ({
              value: s.id,
              label: `${s.screen_name} (${s.screen_code})`,
            }))}
          />

          <Select
            placeholder="Filter by Playlist"
            allowClear
            style={{ width: 200 }}
            value={playlistId}
            onChange={setPlaylistId}
            options={playlists.map((p) => ({ value: p.id, label: p.name }))}
          />

          <Space>
            <Switch checked={expiringSoon} onChange={setExpiringSoon} />
            <span>Expiring within 3 days</span>
          </Space>

          <Button icon={<ReloadOutlined />} onClick={fetchReport}>
            Refresh
          </Button>

          <Button
            icon={<FileExcelOutlined />}
            onClick={handleExportExcel}
            disabled={data.length === 0}
          >
            Export Excel
          </Button>

          <Button
            icon={<FilePdfOutlined />}
            onClick={handleExportPDF}
            disabled={data.length === 0}
          >
            Export PDF
          </Button>
        </Space>
      </Card>

      <Card>
        <Table
          rowKey={(record) => `${record.schedule_id}-${record.video_id}`}
          columns={columns}
          dataSource={data}
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>
    </div>
  );
}
